import React from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { IconNameToComponent, IconType } from '../components/Icons';
import {
  EXTENSIONS_LEARN_MORE_LINK,
  FILES_PAGE,
  FORMS_PAGE,
  KNOWLEDGE_BASE_PAGE,
  MESSAGES_PAGE,
  ModuleType,
  MODULE_MANAGEMENT_LIST,
  PAYMENTS_PAGE,
} from '../constants';
import { ModuleListItem } from '../constants/dataTypes';
import { getModuleIcon, getModuleName } from '../utils/ModuleSettingsUtils';
import { RootState } from '../store';

export const defaultModules: ModuleListItem[] = [
  {
    id: 'messages',
    type: 'module',
    title: MESSAGES_PAGE.label,
    Icon: MESSAGES_PAGE.icon,
    fieldName: 'messages',
    iconName: 'messages',
    learnMoreLink: MODULE_MANAGEMENT_LIST.messages.learnMoreLink,
  },
  {
    id: 'files',
    type: 'module',
    title: FILES_PAGE.label,
    Icon: FILES_PAGE.icon,
    fieldName: 'files',
    iconName: 'files',
    learnMoreLink: MODULE_MANAGEMENT_LIST.files.learnMoreLink,
  },
  {
    id: 'forms',
    type: 'module',
    title: FORMS_PAGE.label,
    Icon: FORMS_PAGE.icon,
    fieldName: 'forms',
    iconName: 'forms',
    learnMoreLink: MODULE_MANAGEMENT_LIST.forms.learnMoreLink,
  },
  {
    id: 'payments',
    type: 'module',
    title: PAYMENTS_PAGE.label,
    Icon: PAYMENTS_PAGE.icon,
    fieldName: 'payments',
    iconName: 'payments',
    learnMoreLink: MODULE_MANAGEMENT_LIST.payments.learnMoreLink,
  },
  {
    id: 'knowledgeBase',
    type: 'module',
    title: KNOWLEDGE_BASE_PAGE.label,
    Icon: KNOWLEDGE_BASE_PAGE.icon,
    fieldName: 'knowledgeBase',
    iconName: 'knowledge',
    learnMoreLink: MODULE_MANAGEMENT_LIST.knowledgeBase.learnMoreLink,
  },
];

export type DefaultModule = typeof defaultModules[number];

export const usePortalModulesState: () => ModuleListItem[] = () => {
  const { moduleSettings, extensionsSettings } = useSelector(
    (state: RootState) => ({
      moduleSettings: state.settings.moduleSettings || [],
      extensionsSettings: state.settings.extensionsSettings || {},
    }),
    shallowEqual,
  );
  const portalModulesState = React.useMemo(() => {
    const hasOnlyExtensionsModules =
      moduleSettings &&
      moduleSettings?.every(ms => ms.type === 'local' || ms.type === 'global');
    // if moduleSettings has only extensions append extensions
    // below the default modules.
    if (hasOnlyExtensionsModules || moduleSettings?.length === 0) {
      const extensionsModules = Object.entries(extensionsSettings || {}).map(
        ([key, extensionConfig]) => ({
          id: key,
          type: extensionConfig.type as ModuleType,
          title: extensionConfig.name,
          fieldName: key,
          learnMoreLink: EXTENSIONS_LEARN_MORE_LINK,
          iconName: extensionConfig.icon,
          Icon: IconNameToComponent[extensionConfig.icon],
          isAuthenticated: extensionConfig.isAuthenticated,
        }),
      );
      return defaultModules.concat(extensionsModules);
    }
    const stagedModuleSettingsItems = moduleSettings.map(ms => {
      // when module icon & label are not available in the module object
      // we can get them from the extensions/default modules state.
      const moduleIconName: IconType =
        ms.icon || getModuleIcon(ms.type, ms.id, extensionsSettings);
      const moduleLabel =
        ms.label || getModuleName(ms.type, ms.id, extensionsSettings);

      // if not a core module check if this is an authenticated extension
      const isAuthenticated =
        ms.type !== 'module' &&
        Boolean(
          Object.entries(extensionsSettings || {}).find(
            ([extId]) => extId === ms.id,
          )?.[1].isAuthenticated,
        );
      return {
        id: ms.id,
        type: ms.type as ModuleType,
        title: moduleLabel,
        Icon: IconNameToComponent[moduleIconName],
        iconName: moduleIconName,
        fieldName: ms.id,
        learnMoreLink:
          ms.type === 'module'
            ? defaultModules.find(m => m.id === ms.id)?.learnMoreLink || ''
            : EXTENSIONS_LEARN_MORE_LINK,
        isAuthenticated,
      };
    });

    return stagedModuleSettingsItems;
  }, [extensionsSettings, moduleSettings]);

  return portalModulesState;
};
